//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { ITrackableComponent } from 'unison/dist/src/common/trackable-component';

import Badge, { BadgeType } from '@components/atoms/badge/Badge';
import { Icon } from '@components/atoms/icon/Icon';
import { LinkPrimaryText, MetatagText } from '@components/atoms/text/Text';
import {
  DisabledHarmonyAIIcon,
  HarmonyAIIcon,
  WhiteHarmonyAIIcon,
} from '@components/harmonyai/HarmonyAIIcon';
import { IPopoverOffset } from '@components/molecules/popover/IPopoverOffset';
import Popover from '@components/molecules/popover/Popover';
import { useDialogContext } from '@content-drawer/upload/DialogContextProvider';
import makeAngularReactController from '@react-bridge/angular-react-widget';

const HARMONYAI_HELPLINK =
  'https://docs.bazaarvoice.com/articles/vibe/harmony-ai';

const CURALATE_AI_TAG_FILTERING_LINK =
  'https://support.bazaarvoice.com/s/article/AI-Visual-Tag-Filtering';

export interface HarmonyAITooltipProps extends ITrackableComponent {
  children: ReactNode;
  appendTarget?: HTMLDivElement | null;
  className?: string;
  contentLink?: string;
  icon?: React.ReactNode;
  isDisabled?: boolean;
  isWhiteOnInactive?: boolean;
  offset?: IPopoverOffset;
  shouldShowBetaBadge?: boolean;
}

export const HarmonyAITooltip = ({
  appendTarget,
  children,
  contentLink,
  icon,
  isDisabled = false,
  isWhiteOnInactive = false,
  offset,
  shouldShowBetaBadge = false,
  className,
  trackingId,
}: HarmonyAITooltipProps) => {
  const renderTargetIcon = () => {
    if (icon) {
      return <IconWrapper className={className}>{icon}</IconWrapper>;
    } else if (isDisabled) {
      return <DisabledHarmonyAIIcon className={className} />;
    } else {
      return (
        <Wrapper className={className} isWhiteOnInactive={isWhiteOnInactive}>
          <HarmonyAIIcon />
          <WhiteHarmonyAIIcon />
        </Wrapper>
      );
    }
  };

  return (
    <Popover
      content={
        <Container>
          <Header>
            <HeaderLink href={HARMONYAI_HELPLINK} target='_blank'>
              <HarmonyAIIcon />
              <Spacer />
              BAZAARVOICE HARMONYAI
            </HeaderLink>
          </Header>
          <Content>
            {shouldShowBetaBadge && <BetaBadge />}
            {children}
            {contentLink && (
              <LearnMoreLink
                trackingId={trackingId}
                href={contentLink}
                target='_blank'
              >
                <LinkPrimaryText>Learn More</LinkPrimaryText>
              </LearnMoreLink>
            )}
          </Content>
        </Container>
      }
      offset={offset}
      placement='top'
      isDisabled={isDisabled}
      shouldOpenOnHover={true}
      shouldShowArrow={true}
      appendTarget={appendTarget ? () => appendTarget : 'body'}
      target={renderTargetIcon()}
    />
  );
};

const Container = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    width: calc(${theme.smallMeasure} + ${theme.spacings[2]});
  `,
);

const Header = styled(MetatagText)(
  ({ theme }) => `
    display: flex;
    align-items: center;
    height: 37px;

    // Re-evaluate if theme changes: Should this stay purple or be aliases.secondary?
    background-color: ${theme.colors.purple1};
    padding: ${theme.spacings[2]} ${theme.spacings[3]};
    
    && {
      color: ${theme.colors.white};
    }
  `,
);

const HeaderLink = styled.a`
  display: flex;
  align-items: center;
`;

const Spacer = styled.span(
  ({ theme }) => `
    display: inline-block;
    width: ${theme.spacings[2]};
  `,
);

const Content = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[3]};
  `,
);

const Wrapper = styled.div<{ isWhiteOnInactive: boolean }>(
  ({ isWhiteOnInactive }) => `
    display: inline-block;
    
    ${HarmonyAIIcon} {
      display: ${isWhiteOnInactive ? 'none' : 'inline-block'};
    }
    
    ${WhiteHarmonyAIIcon} {
      display: ${isWhiteOnInactive ? 'inline-block' : 'none'};
    }
    
    ${
      isWhiteOnInactive &&
      `
        :hover {
          ${HarmonyAIIcon} {
            display: inline-block;
          }
          
          ${WhiteHarmonyAIIcon} {
            display: none;
          }
        }
      `
    }
  `,
);

const BetaBadge = styled(Badge).attrs({
  size: 'small',
  type: BadgeType.New,
  text: 'Beta',
})(
  ({ theme }) => `
  margin-right: ${theme.spacings[2]};
  `,
);

const LearnMoreLink = styled.a<ITrackableComponent>`
  display: inline-block;
`;

const IconWrapper = styled.div(
  ({ theme }) => `
    height: 100%;

    span {
      margin-right: ${theme.spacings[0]};
    }
  `,
);

export const TargetIconWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 24px;
`;

const TargetIcon = styled(Icon)(
  ({ theme }) => `
    color: ${theme.colors.green7};
    margin-right: ${theme.spacings[0]};
  `,
);

export const PredictiveSchedulingTooltip = () => {
  const { contentRef } = useDialogContext();

  return (
    <HarmonyAITooltip
      /*
        Append the target to the dialog content ref to resolve the Popover/RadixUI Dialog issue.
        Otherwise, append it to the body (If the ref is not provided or the component is not rendered in the dialog).
      */
      appendTarget={contentRef.current}
      contentLink={CURALATE_AI_TAG_FILTERING_LINK}
      icon={
        <TargetIconWrapper>
          <TargetIcon name='graph-trending-up' />
        </TargetIconWrapper>
      }
      offset={{ distance: 16 }}
      shouldShowBetaBadge={true}
    >
      We think this media may receive higher than average engagement based on
      its visual elements.&nbsp;
    </HarmonyAITooltip>
  );
};

angular.module('curalate').component('predictiveSchedulingTooltip', {
  controller: makeAngularReactController(<PredictiveSchedulingTooltip />),
});
